import { Dispatch, SetStateAction } from 'react';
import { Device } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup.pb';
import { CreateResponse } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { ForwardingNumberTypes } from '@frontend/api-forwarding-number';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { Button, useModalControl, XIcon } from '@frontend/design-system';
import { trackingId } from '../../tracking';
import { LabelsCard } from '../common/labels-card';
import { SettingsCard } from '../common/settings-card';
import { CallLegsWithModal } from './call-legs-with-modal';

export const RingSettings = ({
  callGroup,
  callForwardingNumbers,
  setCallGroupState,
  onSave,
  onRemoveDevice,
  onRingChange,
  onDevicesAssigned,
  disableSave = false,
}: {
  callGroup: CreateResponse;
  callForwardingNumbers: ForwardingNumberTypes.FwdNumberModel[];
  setCallGroupState: Dispatch<SetStateAction<CreateResponse>>;
  onSave?: () => Promise<void>;
  onRemoveDevice: (deviceKey: string) => void;
  onRingChange: (deviceKey: string, delayStart: number, timeout: number) => void;
  onDevicesAssigned: (devices: Device[]) => void;
  disableSave?: boolean;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'call-groups' });
  const assignDeviceModalProps = useModalControl();

  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { selectedGroupId } = useAppScopeStore();

  const isUnify = settingsTenantLocation?.locationType === 'unify';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--fabric-space-gap-md)' }}>
      <SettingsCard
        title={t('Assigned Devices')}
        action={{
          label: t('Assign Device'),
          onClick: () => assignDeviceModalProps.openModal(),
          ref: assignDeviceModalProps.triggerProps.ref,
          trackingId: trackingId({ context: 'setting', feature: 'call-groups', details: 'open-assign-devices' }),
        }}
      >
        <CallLegsWithModal
          callGroup={callGroup}
          callForwardingNumbers={callForwardingNumbers}
          enableDeviceEditing={true}
          tableLayout='separated'
          removeIcon={XIcon}
          externalModalProps={assignDeviceModalProps}
          onRemoveDevice={onRemoveDevice}
          onRingChange={onRingChange}
          onDevicesAssigned={onDevicesAssigned}
        />
      </SettingsCard>

      {/* Work in progress for the below location selector. Using MultiselectField for now but will need to be changed. */}
      {isUnify && !!selectedGroupId && <LabelsCard callGroup={callGroup} setCallGroup={setCallGroupState} />}
      <Button
        css={{ width: 'fit-content' }}
        onClick={onSave}
        disabled={disableSave}
        trackingId={trackingId({ context: 'setting', feature: 'call-groups', details: 'save' })}
      >
        {t('Save Changes')}
      </Button>
    </div>
  );
};
